import env from '@beam-australia/react-env';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import Cookies from 'js-cookie';
import { linkPrefixes, locationInputs } from '@/constants/urls';
import { fetchGuestUser } from '@/users/actions';
import selectors from '@/selectors';
import {
  inProgress,
  userSignedIn,
  userFound,
  initializing,
  initialized,
  idTokenAcquired,
} from './actions';
import { decode } from './token';
import { AuthenticationState } from './constants';

const CLIENT_ID = 'FAIR';

export const POST_SIGNIN_REDIRECT_KEY = 'fairUi.postSigninRedirect';

const getAuthority = () => env('B2C_URI');
const getClientId = () => CLIENT_ID;
const getDomain = () => env('FAIR_UI_HOSTNAME');

const getLoginHint = () => {
  const loginHint = Cookies.get('loginHint');
  const params = locationInputs.params();
  const loginHintParam = params.get('loginHint');
  if (loginHintParam && loginHintParam !== loginHint) {
    const domain = locationInputs.hostParts().slice(1).join('.');
    Cookies.set('loginHint', loginHintParam, {
      expires: 365,
      SameSite: 'Strict',
      Secure: true,
      domain,
    });
    return loginHintParam;
  }
  return loginHint;
};

// To add logging, import Log from oidc-client and uncomment the following two lines
//
// Log.level = Log.DEBUG;
// Log.logger = console;

export class AuthProvider {
  constructor(authority, clientId, baseUri) {
    this.userManager = null;
    this.authority = authority;
    this.clientId = clientId;
    this.baseUri = baseUri;
    this.store = null;
  }

  registerStore(store) {
    this.store = store;
  }

  getUserManager() {
    return this.userManager;
  }

  initializeUserManager() {
    if (!this.store) {
      return;
    } // TODO: add error handling (ie when store has not been registered)

    this.store.dispatch(initializing());
    const settings = {
      authority: this.authority,
      client_id: this.clientId,
      redirect_uri: `${linkPrefixes.HTTPS}${this.baseUri}/#/authcallback`,
      silent_redirect_uri: `${linkPrefixes.HTTPS}${this.baseUri}/#/authcallback`,
      post_logout_redirect_uri: `${linkPrefixes.HTTPS}${this.baseUri}/#/logout`,
      response_type: 'code',
      automaticSilentRenew: true,
      monitorSession: true,
      revokeAccessTokenOnSignout: true,
      userStore: new WebStorageStateStore({ store: localStorage }),
      extraQueryParams: { kc_idp_hint: getLoginHint() },
    };
    this.store.dispatch(initialized());
    this.userManager = new UserManager(settings);
  }

  isCommonsMode() {
    const { auth } = this.store.getState();
    return auth.commonsMode;
  }

  async authenticate() {
    await this.userManager.clearStaleState();
    const user = await this.userManager.getUser();
    if (this.isCommonsMode() && !user) {
      this.store.dispatch(fetchGuestUser());
      this.loginSilent().then(this.postLogin.bind(this)).catch(() => {
        // eat the error, silent login failed, means the user is not signed into keycloak
      });
    } else if (!user || user.expired) {
      // user is not signed in
      this.login();
    } else {
      // user is signed in
      this.postLogin(user);
    }
  }

  postLogin(user) {
    if (user != null) {
      this.store.dispatch(userSignedIn());
      this.store.dispatch(idTokenAcquired(user.id_token));
      this.store.dispatch(userFound(user));
      this.userManager.startSilentRenew();
    }
  }

  async postReauthenticate(user) {
    const { currentUser: staleUser } = selectors.auth(this.store.getState());
    const decoded = await decode(user.id_token);
    const { email } = decoded;

    if (staleUser && staleUser.email.toLowerCase() === email.toLowerCase()) {
      this.store.dispatch(userSignedIn());
      this.store.dispatch(idTokenAcquired(user.id_token));
    } else {
      this.postLogin(user);
    }
  }

  async getAccount() {
    const user = await this.userManager.getUser();
    return user;
  }

  logout() {
    return this.userManager.signoutRedirect();
  }

  async getIdToken() {
    const user = await this.userManager.getUser();
    if (user) {
      return user.id_token;
    }
    return null;
  }

  async getIsAuthenticated() {
    const { state } = selectors.auth(this.store.getState());

    return AuthenticationState.Authenticated === state;
  }

  login() {
    this.store.dispatch(inProgress());
    const currentUrl = window.location.href;
    const onAuthCallbackPage = locationInputs.hash().startsWith('authcallback');
    let postSignInRedirectUri = currentUrl;
    if (onAuthCallbackPage) {
      postSignInRedirectUri = sessionStorage.getItem(POST_SIGNIN_REDIRECT_KEY) || '#';
    } else {
      sessionStorage.setItem(POST_SIGNIN_REDIRECT_KEY, currentUrl);
    }
    this.userManager.signinRedirect({
      state: { post_signin_redirect_uri: postSignInRedirectUri },
    });
  }

  /**
   * Opens up a "pop up" for the user to authenticate.
   * @returns {Promise} Promise fulfilled once user logs in.
   */
  loginWithPopup() {
    return this.userManager.signinPopup();
  }

  /**
   * Silently attempts to log in a user (no UI action needed).
   * @returns {Promise} Promise fulfilled once user logs in.
   */
  loginSilent() {
    return this.userManager.signinSilent();
  }

  async loginSilentOrWithPopup() {
    try {
      return await this.loginSilent();
    } catch {
      window.console.error('Silent login failed, falling back to pop up');
      return this.loginWithPopup();
    }
  }

  /**
   * This is good when we get 401 due to some token expiration/invalidation.
   * Tries to silently get a valid token, if that fails,
   * it opens a pop up for the user to reenter their credentials.
   */
  async reauthenticateUser() {
    const user = await this.loginSilentOrWithPopup();

    await this.postReauthenticate(user);

    return user;
  }
}

let provider = null;
const authProviderSingleton = () => {
  if (provider) {
    return provider;
  }
  provider = new AuthProvider(getAuthority(), getClientId(), getDomain());
  return provider;
};

export default authProviderSingleton;
